import { awardEnrichmentApi } from 'components/loyalty/AwardEnrichmentsApi';
import { IAwardEnrichment } from 'components/loyalty/models/AwardEnrichment';
import { updateArrayItem } from 'lib/helpers';
import React from 'react';

export const ITEMS_PER_PAGE = 100;
const DEFAULT_QUERY = { limit: ITEMS_PER_PAGE };

export const useExternalPerksData = () => {
    const [loading, setLoading] = React.useState(false);

    const [awardEnrichments, setAwardEnrichments] = React.useState<IAwardEnrichment[]>([]);
    const [page, setPage] = React.useState(0);
    const [pages, setPages] = React.useState([undefined]);
    const [nextKey, setNextKey] = React.useState<string>();
    const [endOfData, setEndOfData] = React.useState(false);

    const count = React.useMemo(() => {
        if (endOfData) {
            return awardEnrichments.length;
        }

        return -1;
    }, [endOfData, awardEnrichments]);

    const data = React.useMemo(
        () => awardEnrichments.slice(page * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE + ITEMS_PER_PAGE),
        [page, awardEnrichments]
    );

    const handlePageChange = React.useCallback(
        (_: never, page: number) => {
            if (!loading) {
                setPage(page);
            }
        },
        [loading]
    );

    React.useEffect(() => {
        const load = async () => {
            setLoading(true);

            const query = page > 0 ? { ...DEFAULT_QUERY, startKey: pages[page] } : DEFAULT_QUERY;

            const res = await awardEnrichmentApi.getAllAwardEnrichments(query);

            if (!res.ok || !res.body.items) {
                throw new Error(res.body.message);
            }

            const {
                page: { nextKey },
                items
            } = res.body;

            if (!nextKey) {
                setEndOfData(true);
            }

            setPages(currentPages => updateArrayItem(currentPages, page + 1, nextKey));

            setAwardEnrichments(items);
            setNextKey(nextKey);

            setLoading(false);
        };

        if (!loading && !endOfData && ((page === 0 && awardEnrichments.length === 0) || !pages[page + 1])) {
            load();
        }
    }, [awardEnrichments.length, endOfData, loading, page, pages]);

    return {
        data,
        count,
        page,
        nextKey,
        loading,
        awardEnrichments,
        setAwardEnrichments,
        handlePageChange
    };
};
